import { getBatchCandidates, getSingleWarehouseStock, type BatchCandidate } from '@api/inventory';
import { getWmsPushPlan, type WmsPushPlan } from '@api/mother-order';
import { listSalesVendors, listVendors } from '@api/vendors';
import { createStn, listWarehousesForStn, pushStnToWms, type CreateStnItemInput, type WarehousePickItem } from '@api/warehouse-ops';
import useToaster from '@hooks/toaster/useToaster';
import {
    ActionIcon,
    Button,
    Checkbox,
    Divider,
    Drawer,
    Group,
    NumberInput,
    SegmentedControl,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { Plus, Trash } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FC } from 'react';

type Props = { opened: boolean; onClose: () => void };

type ItemRow = { itemId: string; plannedQty: number; batchId?: string };

/**
 * The three kinds of stock transfer. Source is one of our own warehouses; the destination
 * picker changes with the kind, and so does what a WMS push creates: STOCK_TRANSFER gate pass
 * to another facility, NON_RETURNABLE to a vendor or channel party.
 *
 * FROM_VENDOR is a fourth way to raise one, not a fourth kind: raw or packaging material the
 * brand holds at a vendor site (a manufacturer's or supplier's) coming back to one of its own
 * warehouses. It is an INTERNAL transfer whose source is that site; a vendor site is no WMS
 * facility, so it is never pushed.
 */
type TransferKind = 'INTERNAL' | 'CHANNEL' | 'VENDOR';
type TransferMode = TransferKind | 'FROM_VENDOR';
const NO_WMS_PLAN: WmsPushPlan = { wmsType: null, mode: null, order: null, shipment: null, receive: null, standaloneShipment: null, standaloneReceive: null, stn: null, distribution: null };

// Group the STN node pickers by warehouse type — self-operated nodes first
// (Internal, Retail), then channel/marketplace FCs (Amazon/Zepto/etc.).
const TYPE_GROUP_LABELS: Record<string, string> = {
    INTERNAL: 'Internal',
    RETAIL: 'Retail',
    AMAZON: 'Amazon',
    QUICK_COMMERCE: 'Quick Commerce',
    E_COMMERCE: 'E-Commerce',
    MANUFACTURER: 'Manufacturer',
    ASSEMBLY: 'Assembly',
    SUPPLIER: 'Supplier',
    DISTRIBUTOR: 'Distributor',
};
const TYPE_GROUP_ORDER = ['INTERNAL', 'RETAIL', 'AMAZON', 'QUICK_COMMERCE', 'E_COMMERCE'];

const buildWarehouseGroups = (warehouses: WarehousePickItem[], excludeId?: string) => {
    const byType = new Map<string, Array<{ value: string; label: string }>>();
    for (const w of warehouses) {
        if (w.warehouseId === excludeId) continue;
        const items = byType.get(w.type) ?? [];
        items.push({ value: w.warehouseId, label: w.name });
        byType.set(w.type, items);
    }
    const knownTypes = TYPE_GROUP_ORDER.filter((t) => byType.has(t));
    const otherTypes = [...byType.keys()].filter((t) => !TYPE_GROUP_ORDER.includes(t)).sort();
    return [...knownTypes, ...otherTypes].map((type) => ({
        group: TYPE_GROUP_LABELS[type] ?? type,
        items: (byType.get(type) ?? []).sort((a, b) => a.label.localeCompare(b.label)),
    }));
};

const CreateStnDrawer: FC<Props> = ({ opened, onClose }) => {
    const queryClient = useQueryClient();
    const { showToastSuccess, showToastError } = useToaster();
    const [transferMode, setTransferMode] = useState<TransferMode>('INTERNAL');
    const fromVendor = transferMode === 'FROM_VENDOR';
    const transferKind: TransferKind = fromVendor ? 'INTERNAL' : transferMode;
    const [fromNodeId, setFromNodeId] = useState('');
    const [toNodeId, setToNodeId] = useState('');
    const [pushToWms, setPushToWms] = useState(false);
    const [itemType, setItemType] = useState<'product' | 'material'>('product');
    const [workOrderId, setWorkOrderId] = useState('');
    const [notes, setNotes] = useState('');
    const [plannedDispatch, setPlannedDispatch] = useState<Date | null>(null);
    const [items, setItems] = useState<ItemRow[]>([{ itemId: '', plannedQty: 0 }]);

    const warehousesQuery = useQuery<WarehousePickItem[]>({
        queryKey: ['warehouses-picker-stn'],
        queryFn: () => listWarehousesForStn(),
    });

    // VENDOR → purchase vendors; CHANNEL → the channel parties kept as SALES_VENDOR vendors.
    const vendorsQuery = useQuery({
        queryKey: ['vendors-picker-stn', transferKind],
        queryFn: () => (transferKind === 'CHANNEL' ? listSalesVendors() : listVendors()),
        enabled: transferKind !== 'INTERNAL',
    });
    const { data: wmsPlanData } = useQuery(['wms-push-plan'], getWmsPushPlan, { staleTime: 5 * 60_000 });
    const wmsPlan = wmsPlanData ?? NO_WMS_PLAN;

    const warehouses = warehousesQuery.data ?? [];
    // Stock leaves one of OUR facilities, or, coming back, a vendor site holding our material.
    const fromNodeOptions = useMemo(
        () =>
            buildWarehouseGroups(
                warehouses.filter((w) => (fromVendor ? !!w.vendorId : w.type === 'INTERNAL' && !w.vendorId)),
            ),
        [warehouses, fromVendor],
    );
    const toNodeOptions = useMemo(() => {
        if (transferKind !== 'INTERNAL') {
            return (vendorsQuery.data ?? [])
                .map((v) => ({ value: v.vendorId, label: v.name && v.name !== v.vendorId ? `${v.name} (${v.vendorId})` : v.vendorId }))
                .sort((a, b) => a.label.localeCompare(b.label));
        }
        return buildWarehouseGroups(warehouses.filter((w) => w.type === 'INTERNAL' && !w.vendorId), fromNodeId);
    }, [transferKind, warehouses, vendorsQuery.data, fromNodeId]);

    const destination = useMemo(() => {
        if (!toNodeId) return null;
        if (transferKind !== 'INTERNAL') {
            const v = (vendorsQuery.data ?? []).find((x) => x.vendorId === toNodeId);
            // A vendor id IS the Uniware party code.
            return v ? { name: v.name || v.vendorId, wmsCode: v.vendorId } : null;
        }
        const w = warehouses.find((x) => x.warehouseId === toNodeId);
        return w ? { name: w.name, wmsCode: w.facilityId ?? null } : null;
    }, [toNodeId, transferKind, warehouses, vendorsQuery.data]);
    // What the push would create, in words — and whether it can be created at all.
    const wmsGatePassType = transferKind === 'INTERNAL' ? 'STOCK_TRANSFER' : 'NON_RETURNABLE';
    const wmsBlocked = !!destination && !destination.wmsCode;
    const wmsLabel = destination
        ? `Push to WMS — will create a ${wmsGatePassType} gate pass to ${destination.name}${destination.wmsCode ? ` (${destination.wmsCode})` : ''}`
        : 'Push to WMS — will create a gate pass';

    /**
     * Only what is on the shelf at the source can leave it, so the picker lists the source's
     * stock of this item type, with how much is there. Nothing to pick until a source is chosen.
     */
    const sourceStockQuery = useQuery({
        queryKey: ['stn-source-stock', fromNodeId, itemType],
        queryFn: () => getSingleWarehouseStock(fromNodeId, { itemType, fetchAll: true }),
        enabled: !!fromNodeId,
    });

    // itemId stored on the STN is the product/material id (not the SKU). Label mirrors the PO
    // picker, "SKU — NAME", plus the quantity on hand at the source.
    const itemOptions = useMemo(
        () =>
            (sourceStockQuery.data?.data ?? [])
                .filter((stock) => stock.product_id && stock.quantity > 0)
                .map((stock) => {
                    const name = stock.product_name ?? stock.product_id;
                    return {
                        value: stock.product_id,
                        label: `${stock.sku ? `${stock.sku} — ${name}` : name} · ${stock.quantity.toLocaleString('en-IN')} on hand`,
                    };
                }),
        [sourceStockQuery.data],
    );

    /**
     * The batches of each picked item at the source, in the merchant's picking order (FEFO or
     * FIFO, an org setting). Fetched per distinct item once a source is chosen; an item with no
     * batch rows offers no picker and moves from the no-batch bucket as before.
     */
    const distinctItemIds = useMemo(
        () => [...new Set(items.map((i) => i.itemId).filter(Boolean))],
        [items],
    );
    const batchCandidatesQuery = useQuery({
        queryKey: ['stn-batch-candidates', fromNodeId, itemType, distinctItemIds],
        enabled: !!fromNodeId && distinctItemIds.length > 0,
        queryFn: async () => {
            const entries = await Promise.all(
                distinctItemIds.map(async (itemId) => {
                    const rows = await getBatchCandidates({ warehouseId: fromNodeId, itemId, itemType });
                    return [itemId, rows] as const;
                }),
            );
            return new Map<string, BatchCandidate[]>(entries);
        },
    });
    const batchOptionsFor = (itemId: string) => {
        const rows = (batchCandidatesQuery.data?.get(itemId) ?? []).filter((r) => r.batchId);
        return rows.map((r, idx) => ({
            value: r.batchId,
            label: `${r.batchCode ?? r.batchId}${r.expiryDate ? ` · exp ${r.expiryDate}` : ''} · ${r.availableQuantity}${idx === 0 ? ' · next out' : ''}`,
        }));
    };

    /**
     * A row asking for more than the source holds: per item against the stock on hand (two rows
     * of one item leave the same shelf, so they are summed), and per picked batch against what
     * that batch has left. The server makes the same check; this says so before the click.
     */
    const shortfallFor = useMemo(() => {
        const onHand = new Map((sourceStockQuery.data?.data ?? []).map((stock) => [stock.product_id, stock.quantity]));
        const askedByItem = new Map<string, number>();
        const askedByBatch = new Map<string, number>();
        for (const row of items) {
            if (!row.itemId) continue;
            askedByItem.set(row.itemId, (askedByItem.get(row.itemId) ?? 0) + row.plannedQty);
            if (row.batchId) {
                const key = `${row.itemId}|${row.batchId}`;
                askedByBatch.set(key, (askedByBatch.get(key) ?? 0) + row.plannedQty);
            }
        }
        const qty = (value: number) => value.toLocaleString('en-IN');
        return (row: ItemRow): string | null => {
            if (!row.itemId || !sourceStockQuery.data) return null;
            const held = onHand.get(row.itemId) ?? 0;
            const asked = askedByItem.get(row.itemId) ?? 0;
            if (asked > held) return `Only ${qty(held)} on hand here; ${qty(asked)} asked`;
            if (row.batchId) {
                const batch = batchCandidatesQuery.data?.get(row.itemId)?.find((candidate) => candidate.batchId === row.batchId);
                const left = batch?.availableQuantity ?? 0;
                const askedOfBatch = askedByBatch.get(`${row.itemId}|${row.batchId}`) ?? 0;
                if (batch && askedOfBatch > left) return `Only ${qty(left)} left in this batch; ${qty(askedOfBatch)} asked`;
            }
            return null;
        };
    }, [items, sourceStockQuery.data, batchCandidatesQuery.data]);
    const overStock = items.some((row) => shortfallFor(row) !== null);

    const createMutation = useMutation({
        mutationFn: async () => {
            const created = await createStn({
                fromNodeId,
                toNodeId,
                transferKind,
                itemType,
                workOrderId: workOrderId.trim() || undefined,
                notes: notes.trim() || undefined,
                plannedDispatchDate: plannedDispatch ? `${plannedDispatch.getFullYear()}-${String(plannedDispatch.getMonth() + 1).padStart(2, '0')}-${String(plannedDispatch.getDate()).padStart(2, '0')}` : undefined,
                items: items
                    .filter((i) => i.itemId.trim() && i.plannedQty > 0)
                    .map((i) => ({
                        itemId: i.itemId,
                        plannedQty: i.plannedQty,
                        ...(i.batchId ? { batchId: i.batchId } : {}),
                    })) as CreateStnItemInput[],
            });
            // The external action is a second step on purpose: the STN is ours the moment it is
            // saved, and a WMS refusal must not read as "STN failed".
            if (pushToWms && wmsPlan.stn && !wmsBlocked) {
                try {
                    await pushStnToWms(created.id);
                    showToastSuccess({ title: `Gate pass ${created.code} created in WMS` });
                } catch (e: any) {
                    showToastError({
                        title: 'STN saved, but the WMS push failed',
                        subtitle: e?.error?.message || e?.message || 'Retry from the STN page.',
                    });
                }
            }
            return created;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['stns'] });
            handleClose();
        },
        onError: (e: any) =>
            showToastError({ title: 'Could not create the STN', subtitle: e?.error?.message || e?.message || 'Please retry.' }),
    });

    const handleClose = () => {
        setTransferMode('INTERNAL');
        setFromNodeId('');
        setToNodeId('');
        setPushToWms(false);
        setItemType('product');
        setWorkOrderId('');
        setNotes('');
        setItems([{ itemId: '', plannedQty: 0 }]);
        onClose();
    };

    const addItem = () => setItems((prev) => [...prev, { itemId: '', plannedQty: 0 }]);
    const removeItem = (i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i));
    const updateItem = (i: number, field: keyof ItemRow, value: string | number) =>
        setItems((prev) => prev.map((row, idx) => idx === i ? { ...row, [field]: value } : row));

    const isValid =
        fromNodeId &&
        toNodeId &&
        fromNodeId !== toNodeId &&
        items.some((i) => i.itemId.trim() && i.plannedQty > 0) &&
        !overStock;

    return (
        <Drawer
            opened={opened}
            onClose={handleClose}
            title="Create Stock Transfer Note"
            position="right"
            // Wide enough for the four transfer types side by side.
            size="xl"
        >
            <Stack gap="md">
                <div>
                    <Text size="sm" fw={500} mb={4}>Transfer type</Text>
                    <SegmentedControl
                        fullWidth
                        value={transferMode}
                        onChange={(v) => {
                            const next = v as TransferMode;
                            // A vendor site and our warehouses are different source lists: what
                            // was picked from one is not on the other.
                            if ((next === 'FROM_VENDOR') !== fromVendor) {
                                setFromNodeId('');
                                setItems([{ itemId: '', plannedQty: 0 }]);
                            }
                            // Only raw and packaging material comes back from a vendor site.
                            if (next === 'FROM_VENDOR') setItemType('material');
                            setTransferMode(next);
                            setToNodeId('');
                            setPushToWms(false);
                        }}
                        data={[
                            { value: 'INTERNAL', label: 'Warehouse → Warehouse' },
                            { value: 'CHANNEL', label: 'Warehouse → Channel' },
                            { value: 'VENDOR', label: 'Warehouse → Vendor' },
                            { value: 'FROM_VENDOR', label: 'Vendor → Warehouse' },
                        ]}
                    />
                </div>
                <Select
                    label={fromVendor ? 'From vendor site' : 'From node'}
                    placeholder={fromVendor ? 'Select the vendor site holding the material' : 'Select source warehouse'}
                    data={fromNodeOptions}
                    value={fromNodeId}
                    onChange={(v) => {
                        setFromNodeId(v ?? '');
                        // What was picked was on the old source's shelf; clear it.
                        setItems((prev) => prev.map((row) => ({ ...row, itemId: '', batchId: undefined })));
                    }}
                    searchable
                    required
                />
                <Select
                    label={transferKind === 'VENDOR' ? 'To vendor' : transferKind === 'CHANNEL' ? 'To channel (sales vendor)' : 'To warehouse'}
                    placeholder={transferKind === 'VENDOR' ? 'Select vendor' : transferKind === 'CHANNEL' ? 'Select channel (sales vendor)' : 'Select destination warehouse'}
                    data={toNodeOptions}
                    value={toNodeId}
                    onChange={(v) => setToNodeId(v ?? '')}
                    searchable
                    required
                />
                <Select
                    label="Item type"
                    disabled={fromVendor}
                    description={fromVendor ? 'Only raw and packaging material comes back from a vendor site' : undefined}
                    data={[
                        { value: 'product', label: 'Finished Goods (Product)' },
                        { value: 'material', label: 'Raw / Packaging Material' },
                    ]}
                    value={itemType}
                    onChange={(v) => {
                        setItemType((v ?? 'product') as 'product' | 'material');
                        // Previously picked ids belong to the old catalog; clear them.
                        setItems((prev) => prev.map((row) => ({ ...row, itemId: '' })));
                    }}
                />
                <TextInput
                    label="Work order ID (optional)"
                    placeholder="WO-2026-0001"
                    value={workOrderId}
                    onChange={(e) => setWorkOrderId(e.target.value)}
                />
                <DateInput
                    label="Dispatch on (optional)"
                    description="Leave empty to send as soon as it is approved. With a date, the note counts as in transit only from that day."
                    placeholder="As soon as approved"
                    value={plannedDispatch}
                    onChange={(v) => setPlannedDispatch(v ? new Date(v) : null)}
                    minDate={new Date()}
                    clearable
                    valueFormat="DD MMM YYYY"
                />
                <Divider label="Items" />
                <Stack gap="xs">
                    {items.map((item, i) => (
                        <Stack key={i} gap={2}>
                            <Group gap="xs" align="flex-end">
                                <Select
                                    flex={1}
                                    label={i === 0 ? 'Item ID / SKU' : undefined}
                                    placeholder={
                                        !fromNodeId
                                            ? 'Pick the source warehouse first'
                                            : itemType === 'product'
                                              ? 'Search products by SKU or name'
                                              : 'Search materials by SKU or name'
                                    }
                                    disabled={!fromNodeId}
                                    error={shortfallFor(item) !== null}
                                    data={itemOptions}
                                    value={item.itemId || null}
                                    onChange={(v) => updateItem(i, 'itemId', v ?? '')}
                                    searchable
                                    clearable
                                    nothingFoundMessage={
                                        sourceStockQuery.isLoading ? 'Loading stock...' : 'Nothing in stock here matches'
                                    }
                                    limit={20}
                                />
                                {batchOptionsFor(item.itemId).length > 0 && (
                                    <Select
                                        w={220}
                                        label={i === 0 ? 'Batch' : undefined}
                                        placeholder="Any (no batch)"
                                        data={batchOptionsFor(item.itemId)}
                                        value={item.batchId || null}
                                        onChange={(v) => updateItem(i, 'batchId', v ?? '')}
                                        clearable
                                    />
                                )}
                                <NumberInput
                                    w={110}
                                    label={i === 0 ? 'Qty' : undefined}
                                    placeholder="0"
                                    min={0}
                                    value={item.plannedQty}
                                    onChange={(v) => updateItem(i, 'plannedQty', Number(v) || 0)}
                                    error={shortfallFor(item) !== null}
                                />
                                {items.length > 1 && (
                                    <ActionIcon
                                        color="red"
                                        variant="subtle"
                                        mb={2}
                                        onClick={() => removeItem(i)}
                                    >
                                        <Trash size={16} />
                                    </ActionIcon>
                                )}
                            </Group>
                            {shortfallFor(item) && (
                                <Text size="xs" c="red">{shortfallFor(item)}</Text>
                            )}
                        </Stack>
                    ))}
                    <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<Plus size={12} />}
                        onClick={addItem}
                        w="fit-content"
                    >
                        Add item
                    </Button>
                </Stack>
                <Textarea
                    label="Notes (optional)"
                    placeholder="Any instructions or context"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                />
                {wmsPlan.stn && !fromVendor && (
                    <Checkbox
                        label={wmsLabel}
                        description={
                            wmsBlocked
                                ? 'This destination has no facility code in the WMS.'
                                : 'External action, done after the STN is saved.'
                        }
                        checked={pushToWms && !wmsBlocked}
                        disabled={wmsBlocked || !toNodeId}
                        onChange={(e) => setPushToWms(e.currentTarget.checked)}
                    />
                )}
                <Group justify="flex-end" mt="sm">
                    <Button variant="subtle" onClick={handleClose}>Cancel</Button>
                    <Button
                        disabled={!isValid}
                        loading={createMutation.isPending}
                        onClick={() => createMutation.mutate()}
                    >
                        Create STN
                    </Button>
                </Group>
            </Stack>
        </Drawer>
    );
};

export default CreateStnDrawer;
